'use client';

import { useLayoutEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { AudioLines, Plus, Send } from 'lucide-react';
import { cn } from '@/lib/utils';
import { writeWorkspaceFilesCreationDraft as stageComposerDraft } from '@/lib/workspace-files-create-action';
import { getComposerAffordanceDelays } from './composer-affordance-motion';
import { LandingComposerMenu } from './landing-composer-menu';
import {
  hasMarketingComposerDraft,
  selectMarketingComposerExample,
  shouldSubmitMarketingComposer,
} from './landing-composer-state';
import type { MarketingPageComposer } from './marketing-page-content-types';
import { SectionShell } from './section-shell';

export function LandingComposer({
  composer,
}: {
  composer: MarketingPageComposer;
}) {
  const router = useRouter();
  const [draft, setDraft] = useState('');
  const [mobileUseCasesExpanded, setMobileUseCasesExpanded] = useState(false);
  const leftAffordanceRef = useRef<HTMLButtonElement>(null);
  const rightAffordanceRef = useRef<HTMLAnchorElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hasStartedAffordanceAnimation = useRef(false);
  const hasDraft = hasMarketingComposerDraft(draft);

  const saveDraft = () => {
    try {
      // Reuse the app's one-time, editable draft handoff across auth redirects.
      stageComposerDraft(window.sessionStorage, draft);
    } catch {
      // Storage can be disabled; signup should still remain available.
    }
  };

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = '0px';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
  }, [draft]);

  useLayoutEffect(() => {
    if (hasStartedAffordanceAnimation.current) return;

    const leftAffordance = leftAffordanceRef.current;
    const rightAffordance = rightAffordanceRef.current;
    if (!leftAffordance || !rightAffordance) return;

    hasStartedAffordanceAnimation.current = true;
    const delays = getComposerAffordanceDelays();

    leftAffordance.style.animationDelay = `${delays.left}ms`;
    leftAffordance.classList.remove('md:translate-x-30');
    leftAffordance.classList.add('md:animate-marketing-composer-left');

    rightAffordance.style.animationDelay = `${delays.right}ms`;
    rightAffordance.classList.remove('md:-translate-x-30');
    rightAffordance.classList.add('md:animate-marketing-composer-right');
  }, []);

  return (
    <section className="bg-marketing-bg w-full shrink-0 pb-3 md:pb-24">
      <SectionShell>
        <div className="bg-marketing-panel flex w-full flex-col items-center rounded-lg p-1 md:rounded-4xl md:px-8 md:py-20 lg:py-24">
          <div className="mx-auto flex w-full max-w-250 shrink-0 flex-wrap items-stretch gap-1 md:flex-nowrap md:gap-0.5">
            <LandingComposerMenu composer={composer}>
              <button
                ref={leftAffordanceRef}
                type="button"
                aria-label={composer.addLabel}
                className={cn(
                  affordanceClassName,
                  'focus-visible:outline-marketing-accent-strong hover:bg-marketing-surface order-2 flex-1 cursor-pointer transition-colors outline-none focus-visible:outline-2 focus-visible:outline-offset-2 md:order-1 md:size-28 md:flex-none md:translate-x-30 md:motion-reduce:translate-x-0 md:motion-reduce:animate-none'
                )}
              >
                <AffordanceIcon>
                  <Plus className="size-5" aria-hidden="true" />
                </AffordanceIcon>
                <div
                  aria-hidden="true"
                  className="bg-marketing-field absolute top-1/2 right-0 hidden h-14 w-0.5 translate-x-full -translate-y-1/2 md:block"
                />
              </button>
            </LandingComposerMenu>

            <label className="bg-marketing-field relative z-10 order-1 flex min-h-20 w-full min-w-0 cursor-text items-center rounded-md px-6 py-5 md:order-2 md:w-auto md:flex-1 md:rounded-2xl">
              <textarea
                ref={textareaRef}
                rows={1}
                wrap="soft"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (
                    shouldSubmitMarketingComposer({
                      key: event.key,
                      shiftKey: event.shiftKey,
                      isComposing: event.nativeEvent.isComposing,
                    })
                  ) {
                    event.preventDefault();
                    saveDraft();
                    router.push(composer.href);
                  }
                }}
                aria-label={composer.accessibleLabel}
                placeholder={composer.placeholder}
                className="scrollbar-hide text-marketing-field-fg placeholder:text-marketing-fg-muted max-h-40 min-h-6 w-full max-w-full min-w-0 resize-none overflow-x-hidden overflow-y-auto bg-transparent px-1 text-center text-sm leading-[1.35] [letter-spacing:-0.015em] [overflow-wrap:anywhere] whitespace-pre-wrap outline-none md:text-lg"
              />
            </label>

            <Link
              ref={rightAffordanceRef}
              href={composer.href}
              onClick={saveDraft}
              aria-label={hasDraft ? composer.sendLabel : composer.voiceLabel}
              className={cn(
                affordanceClassName,
                'focus-visible:outline-marketing-accent-strong hover:bg-marketing-surface order-3 flex-1 cursor-pointer transition-colors outline-none focus-visible:outline-2 focus-visible:outline-offset-2 md:size-28 md:flex-none md:-translate-x-30 md:motion-reduce:translate-x-0 md:motion-reduce:animate-none'
              )}
            >
              <div
                aria-hidden="true"
                className="bg-marketing-field absolute top-1/2 left-0 hidden h-14 w-0.5 -translate-x-full -translate-y-1/2 md:block"
              />
              <AffordanceIcon>
                {hasDraft ? (
                  <Send className="size-5" aria-hidden="true" />
                ) : (
                  <AudioLines className="size-5" aria-hidden="true" />
                )}
              </AffordanceIcon>
            </Link>
          </div>

          <button
            type="button"
            id="marketing-composer-examples"
            aria-controls="marketing-composer-example-list"
            aria-expanded={mobileUseCasesExpanded}
            onClick={() => setMobileUseCasesExpanded((expanded) => !expanded)}
            className={cn(
              'focus-visible:outline-marketing-accent-strong mt-1 flex w-full shrink-0 cursor-pointer items-center justify-center rounded-md text-sm font-medium text-white transition-[min-height,background-color] duration-500 ease-out outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 motion-reduce:transition-none md:hidden',
              mobileUseCasesExpanded ? 'min-h-10 bg-neutral-500/70' : 'min-h-20'
            )}
          >
            {composer.useCasesLabel}
          </button>

          <p className="mx-auto mt-8 hidden w-full text-center text-sm font-normal text-white md:block">
            {composer.useCasesLabel}
          </p>

          <ul
            id="marketing-composer-example-list"
            aria-labelledby="marketing-composer-examples"
            className={cn(
              'mx-auto flex min-h-0 w-full flex-1 flex-col justify-center gap-1 overflow-y-auto transition-[max-height,opacity,transform,margin] duration-500 ease-out motion-reduce:transition-none md:mt-4 md:max-h-none md:max-w-194.25 md:translate-y-0 md:flex-row md:flex-wrap md:gap-2 md:overflow-visible md:opacity-100 md:transition-none',
              mobileUseCasesExpanded
                ? 'mt-1 max-h-96 translate-y-0 opacity-100'
                : 'pointer-events-none max-h-0 translate-y-2 overflow-hidden opacity-0 md:pointer-events-auto'
            )}
          >
            {composer.examples.map((example) => {
              const isActive = draft === example.prompt;

              return (
                <li key={example.label} className="w-full md:w-auto">
                  <button
                    type="button"
                    aria-pressed={isActive}
                    onClick={() =>
                      setDraft((current) =>
                        selectMarketingComposerExample(current, example)
                      )
                    }
                    className={cn(
                      'focus-visible:outline-marketing-accent-strong min-h-9 w-full cursor-pointer rounded-sm px-2.5 py-1 text-xs leading-[1.45] font-medium transition-colors outline-none focus-visible:outline-2 focus-visible:outline-offset-2 md:min-h-0 md:w-auto',
                      isActive
                        ? 'bg-marketing-accent-strong text-marketing-accent-fg'
                        : 'bg-neutral-600/50 text-white hover:bg-neutral-500/50'
                    )}
                  >
                    {example.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </SectionShell>
    </section>
  );
}

const affordanceClassName =
  'bg-marketing-chip md:bg-marketing-field relative flex min-h-20 items-center justify-center rounded-md md:rounded-2xl';

function AffordanceIcon({ children }: { children: React.ReactNode }) {
  return (
    <span className="bg-marketing-chip text-marketing-chip-fg flex size-11 items-center justify-center rounded-xl">
      {children}
    </span>
  );
}
